/**
 * WalletGate — the HARD, global wallet gate for the whole authenticated console.
 *
 * After Google sign-in, the user sees NOTHING until they connect the wallet they
 * deposited with. Behaviour:
 *   - account has no bound wallet  → connecting one CLAIMS it (first-connect-wins),
 *     via useAuth().bindWallet; that connected wallet becomes their wallet.
 *   - account already bound        → they must connect that exact address.
 *   - wrong wallet connected       → refuse + offer disconnect.
 *
 * It's a full-screen overlay (not dismissable) so nothing behind it is reachable
 * until the correct wallet is connected. The data API independently enforces
 * own-wallet-only, so the gate is UX, not the security boundary.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { useWallet, useWalletContext, ConnectButton } from "@thema/ui/wallet";
import { useAuth } from "../contexts/AuthContext";
import { Logo } from "./Brand";
import { Callout, Spinner } from "./primitives";
import { shortenAddress } from "../lib/format";

function Overlay({ children }: { children: ReactNode }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50, background: "var(--bg)", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 440, display: "grid", gap: 22, textAlign: "center" }}>
        <div style={{ justifySelf: "center" }}>
          <Logo size={30} />
        </div>
        {children}
      </div>
    </div>
  );
}

export function WalletGate({ children }: { children: ReactNode }) {
  const { boundWallet, bindWallet, logout, email } = useAuth();
  const { address, isConnected } = useWallet();
  const { disconnect } = useWalletContext();

  const connected = isConnected && address ? address.toLowerCase() : null;
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const triedRef = useRef<string | null>(null);

  // First-connect-wins: once a wallet is connected and the account has none,
  // claim it. Guarded by triedRef so a failed claim doesn't loop.
  useEffect(() => {
    if (connected && !boundWallet && !claiming && triedRef.current !== connected) {
      triedRef.current = connected;
      setClaiming(true);
      setClaimError(null);
      bindWallet(connected)
        .catch((e: Error) => setClaimError(e.message))
        .finally(() => setClaiming(false));
    }
  }, [connected, boundWallet, claiming, bindWallet]);

  const passed = !!connected && !!boundWallet && connected === boundWallet;
  if (passed) return <>{children}</>;

  const signOut = (
    <button
      onClick={logout}
      className="mono"
      style={{ appearance: "none", border: "none", background: "none", cursor: "pointer", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--micro)" }}
    >
      Sign out{email ? ` · ${email}` : ""}
    </button>
  );

  // Wrong wallet connected while the account is bound to a different one.
  if (connected && boundWallet && connected !== boundWallet) {
    return (
      <Overlay>
        <div>
          <h1 className="serif" style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Wrong wallet</h1>
          <p style={{ fontSize: 13.5, color: "var(--text-soft)", lineHeight: 1.6, marginTop: 10 }}>
            You're connected as <span className="mono">{shortenAddress(connected)}</span>, but your
            account is linked to <span className="mono">{shortenAddress(boundWallet)}</span>.
            Disconnect and connect the wallet you deposited with.
          </p>
        </div>
        <button className="btn" style={{ justifySelf: "center" }} onClick={disconnect}>Disconnect</button>
        {signOut}
      </Overlay>
    );
  }

  // Claiming the just-connected wallet.
  if (claiming) {
    return (
      <Overlay>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: "var(--text-soft)", fontSize: 14 }}>
          <Spinner size={16} /> Linking your wallet…
        </div>
        {signOut}
      </Overlay>
    );
  }

  // Not connected yet (or a claim failed) — the primary "connect your deposit
  // wallet" hard prompt.
  return (
    <Overlay>
      <div>
        <h1 className="serif" style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Connect your wallet</h1>
        <p style={{ fontSize: 14, color: "var(--text-soft)", lineHeight: 1.65, marginTop: 12 }}>
          Connect the wallet which you used to deposit funds in the Thema Vault. Your analytics are
          shown only for that wallet.
        </p>
      </div>
      {claimError && <Callout tone="error">{claimError}</Callout>}
      <div style={{ justifySelf: "center" }}>
        <ConnectButton variant="primary" size="lg" connectLabel="Connect wallet" />
      </div>
      {signOut}
    </Overlay>
  );
}
